import classNames from "classnames";
import { InputRoot } from "@design-system/Input/InputParts";
import { resolveChevronMode } from "./PickerContext";
import {
    PickerInputTriggerBody,
    useInputTriggerRoot,
} from "./PickerInputTriggerParts";
import type { PickerInputTriggerProps } from "./Picker.types";
import styles from "./PickerInputTrigger.module.scss";

/**
 * Input-as-trigger preset on the Input primitive parts: Base UI's
 * `Combobox.Input` as the control, an optional leading selection summary for
 * multi-select roots, an optional clear button and a trailing chevron that is
 * the Base UI trigger. Border, focus ring and invalid chrome come from Input,
 * and the popup anchors to this root so it lines up with the field border.
 */
export const PickerInputTrigger = ({
    placeholder,
    size = "md",
    showChevron,
    clearable = false,
    summary,
    ref,
    className,
    onKeyDown,
    onBlur,
    "data-forced-state": forcedState,
}: PickerInputTriggerProps) => {
    const root = useInputTriggerRoot("Picker.InputTrigger");
    const chevronMode = resolveChevronMode({ showChevron, root });

    return (
        <InputRoot
            ref={root.setTriggerAnchor}
            variant="primary"
            size={size}
            disabled={root.isDisabled}
            className={classNames(
                styles.inputTrigger,
                root.classNames.trigger,
                className,
            )}
            data-picker-part="input-trigger"
            data-picker-trigger={root.chainId}
            data-forced-state={forcedState ?? root.forcedState}
            data-chevron={chevronMode}
        >
            <PickerInputTriggerBody
                placeholder={placeholder}
                showChevron={chevronMode !== "none"}
                clearable={clearable}
                summary={summary}
                chevronClassName={styles.inputTriggerChevronGlyph}
                ref={ref}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
            />
        </InputRoot>
    );
};
